import { Component } from 'preact';
import { onConnectionState, isSessionSuperseded } from '../utils/serverClient';
import { isWeb } from '../utils/platform';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, ACCENT_GOLD, DIALOG_STYLE, BEVELED_BTN, FourCorners,
} from '../lib/medievalTheme';
import RuneSpinner from './RuneSpinner';
import { t } from '../localization/t';

function quitApp() {
  fetch('/api/app/quit', { method: 'POST' }).catch(() => {});
}

function reloadApp() {
  window.location.reload();
}

const OVERLAY_BTN_STYLE = {
  ...BEVELED_BTN,
  padding: '0.7cqw 1.4cqw',
  fontSize: '0.78cqw',
  cursor: 'pointer',
};

// Shown when the WebSocket has dropped after a successful initial
// connect. Hidden during the very first connect (LoginScreen renders
// its own loading state for that). A short grace period suppresses
// flicker for ~2s blips that the underlying reconnect logic already
// recovers from before the user notices.

const GRACE_MS = 2500;

export default class ConnectionLostOverlay extends Component {
  state = { visible: false };

  componentDidMount() {
    this._unsub = onConnectionState((state, { hasEverConnected }) => {
      if (!hasEverConnected) return;
      if (state === 'open') {
        if (this._graceTimer) {
          clearTimeout(this._graceTimer);
          this._graceTimer = null;
        }
        this.setState({ visible: false });
        return;
      }
      // A superseded session is not a lost connection: the reconnect
      // loop is latched off and SessionSupersededOverlay owns the
      // screen. Showing "Reconnecting…" here would be a lie.
      if (isSessionSuperseded()) return;
      if (this._graceTimer) return;
      this._graceTimer = setTimeout(() => {
        this._graceTimer = null;
        if (isSessionSuperseded()) return;
        this.setState({ visible: true });
      }, GRACE_MS);
    });
  }

  componentWillUnmount() {
    if (this._unsub) this._unsub();
    if (this._graceTimer) clearTimeout(this._graceTimer);
  }

  render() {
    if (!this.state.visible) return null;
    return (
      <div
        className="fixed inset-0 z-[350] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
      >
        <div className="relative w-full max-w-[30cqw] p-7 text-center" style={DIALOG_STYLE}>
          <FourCorners />
          <div
            className="text-[0.625cqw] font-semibold mb-3"
            style={{ color: `${GOLD} 0.6)` }}
          >
            {t('Connection Lost')}
          </div>
          <h2
            className="text-xl font-bold arena-heading mb-4"
            style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
          >
            {t('Reconnecting…')}
          </h2>
          <div className="flex justify-center mb-4">
            <RuneSpinner />
          </div>
          <p className="text-sm leading-relaxed mb-5" style={{ color: TEXT_BODY }}>
            {t('The connection to the server was interrupted. Your match will resume automatically as soon as the link is restored.')}
          </p>

          {/* Escape-hatch actions so a stuck player isn't trapped
              watching the spinner. The auto-reconnect should handle
              most cases, but giving them a manual reload + a quit
              option keeps the modal from feeling like a dead-end. */}
          <div className="flex justify-center gap-3">
            <button
              type="button"
              className="arena-heading font-semibold"
              style={{ ...OVERLAY_BTN_STYLE, color: ACCENT_GOLD }}
              onClick={reloadApp}
            >
              {t('Retry Now')}
            </button>
            {isWeb() ? null : (
              <button
                type="button"
                className="arena-heading font-semibold"
                style={{ ...OVERLAY_BTN_STYLE, color: TEXT_BODY }}
                onClick={quitApp}
              >
                {t('Quit')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
